<template>
    <div style="width: 100%">
        <oss-data-table
            v-model="selectedAccountingTransactionKeys"
            :headers="headers"
            :items="accountingTransactions"
            :items-per-page="itemPerPage"
            :items-per-page-options="itemPerPages"
            :hide-default-footer="accountingTransactions.length <= 50"
            :loading="loading"
            :striped="true"
            :x-dense="true"
            :height="tableHeight"
            fixed-header
            :fixed-footer="true"
            :virtual-scroll="true"
            :scroll-to-btn-config="{
                up: true,
                down: true,
            }"
            :table-hover="true"
            :displayRowNumber="true"
            :show-select="addCheckBoxColumn"
            item-key="index"
            @clickRow="openAccountingTransaction($event)"
        >
            <template v-slot:header.wording>
                <v-select
                    v-model="tag"
                    :items="tags"
                    item-value="id"
                    v-if="tags.length"
                    hide-details
                    :full-width="false"
                    density="compact"
                    item-title="name"
                >
                    <template v-slot:selection="{ item }">
                        <v-chip-tag>
                            <span :style="'color:' + labelTextColor + ';'">{{ item.title }}</span>
                        </v-chip-tag>
                    </template>
                </v-select>
                <span v-else> Libellé </span>
            </template>
            <template v-slot:item.log="{ item }">
                {{ getItemLog(item) }}
            </template>
            <template v-slot:item.date="{ item }">
                {{ formatDate(item.date) }}
            </template>
            <template v-slot:item.debit="{ item }">
                {{ newCommonFormatCurrency(item.debit, undefined) || "" }}
            </template>
            <template v-slot:item.credit="{ item }">
                {{ newCommonFormatCurrency(item.credit, undefined) || "" }}
            </template>
            <template v-slot:item.paymentDate="{ item }">
                {{ item.paymentDate ? formatDate(item.paymentDate) : "" }}
            </template>
            <template v-slot:item.balance="{ item }">
                {{ getAccountingTransactionBalance(item) || "" }}
            </template>
            <template v-slot:item.lettering="{ item }">
                <template v-if="item.lettering">
                    <span :style="getLetteringColor(item)">{{ item.lettering || "" }}</span>
                </template>
                <v-btn
                    v-if="item.reconciliationToValidate"
                    rounded
                    elevation="0"
                    class="ml-2"
                    @click.stop="validateReconciliation(item.reconciliationId)"
                    size="x-small"
                >
                    Valider
                </v-btn>
                <v-btn-icon
                    icon="$oss-eye"
                    class="ml-2"
                    @click.stop="openAccountingTransaction(item, item.reconciliationId)"
                    v-if="item.reconciliationToValidate"
                />
                <v-scroll-x-transition>
                    <v-chip
                        v-if="candidateToTerminate.find((ctt) => ctt === item)"
                        color="primary"
                        size="x-small"
                    >
                        Candidat
                    </v-chip>
                </v-scroll-x-transition>
            </template>
            <template v-slot:item.wording="{ item }">
                <template v-if="item.tags">
                    <v-chip-tag v-for="(tag, index) of item.tags" :key="index" class="text mr-1">
                        <!-- <v-icon  size="small" left>label</v-icon> -->
                        <span :style="'color:' + labelTextColor + ';'">{{ tag.name }}</span>
                    </v-chip-tag>
                </template>
                <span>
                    {{ item.wording + (item.invoiceNumber ? " - " + item.invoiceNumber : "") }}
                </span>
                <v-chip
                    class="ma-2"
                    color="green"
                    variant="outlined"
                    label
                    size="small"
                    v-if="thirdPartyMode && item.vatDeclarationId"
                >
                    Déclaré
                </v-chip>
            </template>
            <template v-slot:sum.debit="{ sum }">
                {{ debitFormatCurrency(sum) }}
            </template>
            <template v-slot:sum.credit="{ sum }">
                {{ creditFormatCurrency(sum) }}
            </template>
            <template v-slot:sum.balance>
                {{ sumBalance }}
            </template>
            <template v-slot:menu="{ item }">
                <v-list>
                    <v-list-item
                        :disabled="
                            !(isSelectionOkForLettering || isSelectionOkForLetteringWithDelta)
                        "
                        @click.stop="setShowLetteringDialog(false)"
                    >
                        <v-list-item-title>Lettrer</v-list-item-title>
                        <v-list-item-subtitle>
                            <span
                                v-if="
                                    !isSelectionOkForLettering || isSelectionOkForLetteringWithDelta
                                "
                            >
                                (&#xc9;cart:
                                {{
                                    newCommonFormatCurrency(
                                        leterringSelectedTotalAmount,
                                        undefined,
                                    )
                                }})
                            </span>
                        </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item
                        class="oss-list-item"
                        :disabled="!item.lettering"
                        @click.stop="opendDeleteDialog(item)"
                    >
                        <v-list-item-title>Supprimer le lettrage</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        :disabled="!selectedAccountingTransactionCount"
                        @click="openAccountableAccountDialog = true"
                    >
                        <v-list-item-title>Modifier le compte</v-list-item-title>
                    </v-list-item>
                </v-list>
                <!-- <template v-if="displayMenuAccountableAccount">
                    <AccountableAccount
                        :showLabel="true"
                        :showValidation="true"
                        :needValidation="false"
                        :companyId="companyId"
                        :accountableAccountReplacementRule="accountableAccountReplacementRule"
                        :inAccountableAccount="accountableAccountInfo"
                        :disabled="!selectedAccountingTransactionCount"
                        :thirdParty="thirdPartyCompany || user"
                        :defineThirdParty="true"
                        :btn-mode="true"
                        @validated="validAA($event)"
                    ></AccountableAccount>
                </template> -->
            </template>
            <template v-slot:body.append>
                <tr class="x-dense">
                    <!-- :colspan="enableLettering ? (isIncomingOutgoingPayment ? 4:3):(isIncomingOutgoingPayment ? 2:1)" -->
                    <th class="text-start" :colspan="lastFirstColumnColspan">
                        <div class="d-flex align-center">
                            <v-btn-icon
                                icon="$oss-duplicate"
                                color="ternary"
                                @click.stop="copyToClipboard()"
                                class="ml-3"
                                size="x-small"
                            />
                            <multiple-free-tagging
                                :company="selectedCompany"
                                :company-tags="allCompanyTags"
                                :selected-accounting-lines="
                                    selectedAccountingTransactionForTagguing
                                "
                                @selected-tags="onMultiTagguingApplied"
                            />
                            <v-btn-ternary
                                v-if="is471AccountSelected"
                                :disabled="disabledWithoutBankTransactionSelection"
                                @click="
                                    showCreateDocumentMessageDialog =
                                        !showCreateDocumentMessageDialog
                                "
                                icon="$fa-comments"
                            >
                            </v-btn-ternary>
                            <AccountableAccount
                                :title="subAccountableAccountTitle"
                                :showLabel="true"
                                :showValidation="false"
                                :companyId="selectedCompany.id"
                                inAccountableAccountType="Selected"
                                :embeddedInput="true"
                                :editAccountableAccount="true"
                                :inAccountableAccountNames="subSelectedAccountableAccounts"
                                :defineThirdParty="true"
                                :activateFreeTag="true"
                                :bankTransactionId="
                                    selectedAccountingTransactionKeys.length === 1
                                        ? selectedAccountingTransactionKeys[0].id
                                        : ''
                                "
                                @close="closeAccountableAccount"
                                @select-accountableAccount="setAccountableAccount($event)"
                                :key="refreshKey"
                            >
                                <template v-slot:activator="{ props }">
                                    <div class="d-flex align-center">
                                        <v-btn-ternary
                                            class="ml-2"
                                            :disabled="disabledThirdPartyCompany()"
                                            @click="miscelleanousAccountableAccount()"
                                            v-bind="props"
                                        >
                                            Affecter à un compte
                                        </v-btn-ternary>
                                    </div>
                                </template>
                            </AccountableAccount>
                            <v-btn-ternary
                                class="ml-2"
                                @click.stop="openMiscellaneousOperationDialog"
                                :disabled="disabledCreateMO"
                                v-if="!accountableAccountInfo.bnc"
                            >
                                OD
                            </v-btn-ternary>
                            <AccountableAccount
                                :showLabel="true"
                                :needValidation="true"
                                :showValidation="false"
                                :companyId="companyId"
                                :accountableAccountReplacementRule="
                                    accountableAccountReplacementRule
                                "
                                :inAccountableAccount="accountableAccountInfo"
                                :btn-mode="true"
                                :label="'Modifier le compte'"
                                :disabled="
                                    !selectedAccountingTransactionCount || !enableModification
                                "
                                :thirdParty="thirdPartyCompany || user"
                                :defineThirdParty="true"
                                :startOpenDialog="true"
                                @validated="validAA($event)"
                                @dialogClosed="openAccountableAccountDialog = false"
                                v-if="openAccountableAccountDialog"
                            >
                            </AccountableAccount>
                            <v-btn-ternary
                                v-if="!!accountableAccountReplacementRule"
                                class="ml-2"
                                :disabled="
                                    !selectedAccountingTransactionCount || !enableModification
                                "
                                @click="
                                    containsAutomaticAccountingLines
                                        ? (switchToManualConfirmDialog = true)
                                        : (openAccountableAccountDialog = true)
                                "
                            >
                                Modifier le compte
                            </v-btn-ternary>
                            <!-- <template v-if="displayDisconnectAccountingLine">
                                <v-btn
                                    rounded size="small"
                                    color="ternary"
                                    class="mx-2"
                                    elevation="0"
                                    :disabled="disabledDisconnectAccountingLine"
                                    @click.stop="disconnectAccountingLine"
                                >Retirer du compte</v-btn>
                            </template> -->
                            <template v-if="enableThirdPartyCompanyModification">
                                <v-btn-ternary
                                    class="ml-2"
                                    :disabled="
                                        !selectedAccountingTransactionCount || !enableModification
                                    "
                                    @click.stop="dialogSelectCompany = true"
                                >
                                    Modifier le
                                    {{
                                        accountableAccountInfo.name.startsWith("401")
                                            ? "fournisseur"
                                            : "client"
                                    }}
                                </v-btn-ternary>
                                <oss-dialog
                                    v-model="dialogSelectCompany"
                                    :fullscreen="$vuetify.display.mobile"
                                    :title="
                                        'Choisissez le ' +
                                        (accountableAccountInfo.name.startsWith('401')
                                            ? 'fournisseur'
                                            : 'client')
                                    "
                                    v-if="dialogSelectCompany"
                                    @close="thirdPartyCompany = { id: '', name: '', siret: '' }"
                                    @valid="valid"
                                    :validDisabled="!thirdPartyCompany || !thirdPartyCompany.name"
                                    :validLoading="validLoading"
                                    width="800px"
                                >
                                    <company-table
                                        v-model="thirdPartyCompany"
                                        :showGenericCompany="
                                            accountableAccountInfo.name.startsWith('401')
                                        "
                                        :showSupplierCompanies="
                                            accountableAccountInfo.name.startsWith('401')
                                        "
                                        :showConsumerCompany="
                                            accountableAccountInfo.name.startsWith('411')
                                        "
                                        :showCustomerCompanies="
                                            accountableAccountInfo.name.startsWith('411')
                                        "
                                        :showExistingCompanies="false"
                                        :parentCompanyId="companyId"
                                        :wizardMode="true"
                                    />
                                </oss-dialog>
                            </template>
                            <template v-if="enableUserModification">
                                <v-btn-ternary
                                    class="ml-2"
                                    :disabled="
                                        !selectedAccountingTransactionCount || !enableModification
                                    "
                                    @click.stop="userDialog = true"
                                    >Modifier la personne
                                </v-btn-ternary>
                                <oss-dialog
                                    v-model="userDialog"
                                    :fullscreen="$vuetify.display.mobile"
                                    :title="'Choissez une personne'"
                                    v-if="userDialog"
                                    @close="user = { id: '', firstname: '', lastname: '' }"
                                    @valid="valid"
                                    :validDisabled="!user || !user.id"
                                    :validLoading="validLoading"
                                    width="800px"
                                >
                                    <SelectUser v-model="user" :companyId="companyId" />
                                </oss-dialog>
                            </template>

                            <v-btn-ternary
                                class="ml-2"
                                @click.stop="setShowLetteringDialog(true)"
                                v-if="
                                    !accountableAccountInfo.bnc &&
                                    (isSelectionOkForLettering ||
                                        isSelectionOkForLetteringWithDelta)
                                "
                            >
                                <span v-if="isSelectionOkForLettering">Lettrer</span>
                                <span v-else>
                                    Solder (&#xc9;cart:
                                    {{
                                        newCommonFormatCurrency(
                                            leterringSelectedTotalAmount,
                                            undefined,
                                        )
                                    }})
                                </span>
                            </v-btn-ternary>
                            <v-btn-ternary
                                class="ml-2"
                                @click.stop="setShowLetteringDialog(true)"
                                v-if="isIncomingOutgoingPayment && isSelectionOkForBNCLettering"
                            >
                                Rapprocher
                            </v-btn-ternary>

                            <v-btn-ternary
                                class="ml-2"
                                @click.stop="setShowLetteringDialog(false)"
                                v-if="partialLettering"
                                :disabled="!enableModification"
                            >
                                Lettrage Partiel
                            </v-btn-ternary>

                            <v-btn-ternary
                                class="ml-2"
                                @click.stop="validateAllReconciliations()"
                                v-if="massLetteringAllowed"
                            >
                                Lettrer en masse
                            </v-btn-ternary>
                            <!-- <template v-if="isSelectionOkForPartialLettering">
                                <v-btn
                                    class="mx-2"
                                    elevation="0"
                                    rounded
                                     size=“x-small”
                                    color="ternary"
                                    @click.stop="setShowLetteringDialog()"
                                >
                                    Lettrer partiellement
                                </v-btn>
                            </template> -->
                            <v-btn-ternary
                                class="ml-2"
                                @click.stop="setShowDisletteringDialog()"
                                v-if="isSelectionOKForDeleteLettering && enableModification"
                            >
                                Délettrer
                            </v-btn-ternary>
                        </div>
                    </th>
                    <th class="text-right"><strong>Totaux</strong></th>
                    <th class="text-right">
                        <strong>{{
                            newCommonFormatCurrency(Math.abs(totalDebit), undefined)
                        }}</strong>
                    </th>
                    <th class="text-right">
                        <strong>{{
                            newCommonFormatCurrency(Math.abs(totalCredit), undefined)
                        }}</strong>
                    </th>
                    <th class="text-right">
                        <strong :style="getColor(totalBalance)">{{
                            newCommonFormatCurrency(totalBalance, undefined)
                        }}</strong>
                    </th>
                    <th v-if="addEndColumn" />
                </tr>
            </template>
        </oss-data-table>
        <oss-dialog
            v-model="letteringDialog"
            :title="createLetteringTitle"
            @valid="createLettering"
            :validLoading="letteringLoading"
            width="800px"
        >
            <!-- <v-switch inset dense v-model="addLetteringDelta" label="Ajouter l'écart" class="pl-2" v-if="showAddLetteringDeltaSwitch"></v-switch> -->
            <div class="body-1 py-2 text-center">{{ createLetteringAnswer }}</div>
            <v-text-field-filled
                outlined
                v-model="delta.wording"
                label="Libellé"
                v-if="delta && addLetteringDelta"
                density="compact"
                class="pb-2"
            />
            <AccountableAccount
                :inAccountableAccounts="accountableAccounts"
                :thirdParty="delta.thirdPartyCompany || delta.user || delta.bankAccount"
                :show-validation="false"
                hide-details
                :company-id="selectedCompany.id"
                :edit-sub-accountable-account="false"
                label="Compte comptable"
                :inputStyle="'padding: 0'"
                :editAccountableAccount="true"
                @update:model-value="setDeltaAccountableAccount($event)"
                :defineThirdParty="true"
                v-if="delta && accountableAccounts.length && addLetteringDelta"
                :model-value="delta.accountableAccount"
            />
            <oss-data-table
                :items="letteringSelected"
                :headers="letteringHeaders"
                :show-select="false"
                :footer-props="true"
                v-if="delta && !addLetteringDelta"
            >
                <template v-slot:item.letteringSelect="{ item }">
                    <v-icon color="secondary" v-if="item.letteringData.select">$check</v-icon>
                    <!-- <td class="text-right">
                                <v-btn
                                icon
                                 size=“x-small”
                                color="secondary"
                                @click="removeSplitPayment(item)"
                                class="btn"
                                elevation="0"
                                :disabled="splittedPayments.length <= 2"
                            >
                                <v-icon>$oss-trash</v-icon>
                            </v-btn>
                        </td> -->
                </template>
                <template v-slot:item.wording="{ item }">
                    {{ item.wording + (item.invoiceNumber ? " - " + item.invoiceNumber : "") }}
                </template>
                <template v-slot:item.amountSigned="{ item }">
                    {{ newCommonFormatCurrency(item.debit - item.credit, undefined) }}
                </template>
                <template v-slot:item.letteringAmountSigned="{ item }">
                    {{ newCommonFormatCurrency(item.letteringData.amountSigned, undefined) }}
                </template>
            </oss-data-table>
        </oss-dialog>
        <oss-dialog
            v-model="disletteringDialog"
            :title="'Délettrage'"
            @valid="deleteLetterings"
            width="800px"
        >
            <div class="text-center">Délettrer les lignes sélectionnées ?</div>
        </oss-dialog>
        <oss-dialog
            v-model="deleteLetteringDialog"
            :title="'Suppression du lettrage'"
            @valid="deleteLettering"
            :validLoading="deleteLetteringLoading"
            width="800px"
        >
            <h4 class="text-center">&#xca;tes-vous sûr(e) de vouloir supprimer le lettrage ?</h4>
        </oss-dialog>
        <oss-dialog
            v-model="switchResultDialog"
            :title="'Notification'"
            :validTitle="'J\'ai compris'"
            @valid="switchResultDialog = false"
            width="800px"
        >
            <h3
                v-if="
                    switchResult &&
                    switchResult.accountingLines &&
                    switchResult.accountingLines.length === 0
                "
            >
                L'affectation n'a pu être effectuée sur aucune écriture car elle(s) est/sont dans un
                exercice clos ou elle(s) est/sont lettrée(s) avec des écritures d'un exercice clos
            </h3>
            <h3 v-else>
                L'affectation n'a pu être effectuée sur certaines écriture(s) car elle(s) est/sont
                dans un exercice clos ou elle(s) est/sont lettrée(s) avec des écritures d'un
                exercice clos
            </h3>
            <h4>Intangibilité du bilan d'ouverture</h4>
        </oss-dialog>
        <oss-dialog
            v-model="showAccountingTransactionDialog"
            :withoutHeader="true"
            transition="dialog-bottom-transition"
            v-if="showAccountingTransactionDialog"
            @close="closeDocRecDialog()"
            height="100vh"
            width="100vw"
            :hide-valid="showDocument ? true : false"
        >
            <v-row class="d-flex justify-center" v-if="showDocument">
                <v-col cols="12" style="overflow-y: auto">
                    <Document
                        :mode="'GeneralLedger'"
                        :companyId="selectedCompany.id"
                        :id="documentId"
                        :dark-mode="true"
                        @openReconciliation="openReconciliation"
                        @deleted="onDocumentDeleted()"
                        @task-ended="onDocumentUpdated()"
                    />
                </v-col>
            </v-row>
            <Reconciliation
                v-if="showReconciliation"
                :mode="'GeneralLedger'"
                :companyId="selectedCompany.id"
                :documentId="documentId"
                :bankTransactionId="bankTransactionId"
                :reconciliationId="reconciliationId"
                @update="onReconciliationUpdated()"
            />
            <div class="text-center">
                <div class="bottom-sheet">
                    <div class="remote" style="pointer-events: auto">
                        <v-container class="controller elevation-1">
                            <v-btn-prev :disabled="previousDisabled()" @click.stop="previous()" />
                            <v-btn-next :disabled="nextDisabled()" @click.stop="next()" />
                        </v-container>
                    </div>
                </div>
            </div>
        </oss-dialog>
        <!-- <AccountingItemEdition
            v-model="miscellaneousOperationDialog"
            :companyId="companyId"
            :prefilledRows="prefilledRows"
            @upsert="refresh()"
            v-if="miscellaneousOperationDialog"
        ></AccountingItemEdition> -->
        <oss-dialog
            v-model="miscellaneousOperationDialog"
            :title="'Opération diverse'"
            :actions="actionMiscellaneousOperation"
            width="90vw"
        >
            <!-- {{prefilledRows}} -->
            <AccountingItemEdition2
                v-if="miscellaneousOperationDialog"
                :prefilledRows="prefilledRows"
                :companyId="companyId"
                :actions="actionMiscellaneousOperation"
                @upsert="refresh()"
            />
        </oss-dialog>
        <oss-dialog
            v-model="switchToManualConfirmDialog"
            :title="'Mode manuel'"
            @valid="
                openAccountableAccountDialog = true;
                switchToManualConfirmDialog = false;
            "
            width="550px"
        >
            <v-row no-gutters>
                <v-col cols="12" class="d-flex justify-center">
                    <div class="pt-2 px-4 pb-2">
                        Attention, vous allez modifier des écritures automatiques et passer en mode
                        manuel.
                    </div>
                </v-col>
            </v-row>
        </oss-dialog>
        <oss-dialog
            v-model="alertNonAuthorizedDatesDialog"
            title="Date non autorisée"
            validTitle="OK"
            @valid="
                alertNonAuthorizedDatesDialog = false;
                refreshFather();
            "
            width="550px"
        >
            <v-row no-gutters>
                <v-col cols="12" class="d-flex justify-center">
                    <div class="pt-2 px-4 pb-2">
                        Pour information, une ou plusieurs lignes n'ont pas été transférées car le
                        compte bancaire comporte une date de clôture des flux antérieure à la date
                        d'écriture.
                    </div>
                </v-col>
            </v-row>
        </oss-dialog>
        <CreateBTChatMessage
            v-model:active="showCreateDocumentMessageDialog"
            :companyId="selectedCompany.id"
            :bankTransactions="selectedAccountingBankTransactionKeys"
            @message-created="refresh"
        >
        </CreateBTChatMessage>
    </div>
</template>

<script lang="ts">
import moment from "moment-timezone";
import AccountableAccount from "./AccountableAccount.vue";
import CompanyTable from "./admin/CompaniesTable.vue";
import MultipleFreeTagging from "./tag/MultipleFreeTagging.vue";
import Document from "./document/DocumentRouter.vue";
import SelectUser from "./user/SelectUser.vue";
import FreeTag from "./tag/FreeTag.vue";
import AccountingItemEdition2 from "./accounting/accountingItem/Edition.vue";
import OssDataTable from "./ossComponents/ossDataTable2.vue";
import CreateBTChatMessage from "./chat/CreateBTChatMessage.vue";

import { AccountableAccountBalanceDetailBase } from "./AccountableAccountBalanceDetailBase";

import CUSTOMER_COMPANIES from "../graphql/company/CustomerCompanies.gql";
import SUPPLIER_COMPANIES from "../graphql/company/SupplierCompanies.gql";
import BANK_ACCOUNT_FROM_NAME from "../graphql/bank/BankAccountsFromName.gql";
import { DialogAction } from "@/services/dialogAction";
import { Component, Emit, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";
import { Watch } from "vue-facing-decorator";
import Reconciliation from "./Reconciliation.vue";
import { updateBankTransactionAccountableAccount } from "@/services/bankTransactionServices";

// import DELETE_MANY_BANK_TRANSACTION_ACCOUNTING_LINES from "../graphql/accountingLine/DeleteManyBankTransactionAccountingLines.gql"

@Component({
    name: "AccountableAccountBalanceDetail",
    components: {
        AccountableAccount,
        CompanyTable,
        Reconciliation,
        Document,
        SelectUser,
        FreeTag,
        OssDataTable,
        AccountingItemEdition2,
        MultipleFreeTagging,
        CreateBTChatMessage,
    },
})
export class AccountableAccountBalanceDetail extends AccountableAccountBalanceDetailBase {
    @Prop({ default: false }) addEndColumn: string;
    @Prop({ default: null }) fourLastColumnsWidth: any;

    itemPerPages = [10, 20, 50, -1];
    _itemPerPage = 0;

    get activateTagguing() {
        return true;
    }

    get itemPerPage() {
        if (!this._itemPerPage) {
            return this.workingAccountingTransactions.length <= 50 ? -1 : 20;
        }
        return this._itemPerPage;
    }

    set itemPerPage(value) {
        this._itemPerPage = value;
    }

    actionMiscellaneousOperation = {
        action: new DialogAction(),
    };

    sumDebit = 0;

    sumCredit = 0;

    switchToManualConfirmDialog: boolean = false;
    openAccountableAccountDialog: boolean = false;
    displayMenuAccountableAccount: boolean = false;
    showCreateDocumentMessageDialog: boolean = false;
    refreshKey = 0;

    get sumBalance() {
        if (this.sumDebit || this.sumCredit) {
            return this.newCommonFormatCurrency(this.sumDebit - this.sumCredit, undefined);
        }
        return "-";
    }

    get disabledWithoutBankTransactionSelection() {
        return !this.selectedAccountingTransactions.some(
            (at) => at.log === "BANK_TRANSACTION" || at.log === "BANK",
        );
    }

    get selectedAccountingBankTransactionKeys() {
        return this.selectedAccountingTransactionKeys.filter(
            (i) =>
                this.accountingTransactions[i].log === "BANK_TRANSACTION" ||
                this.accountingTransactions[i].log === "BANK",
        );
    }

    debitFormatCurrency(sum) {
        this.sumDebit = sum;
        return this.newCommonFormatCurrency(sum, undefined);
    }

    creditFormatCurrency(sum) {
        this.sumCredit = sum;
        return this.newCommonFormatCurrency(sum, undefined);
    }

    get enableThirdPartyCompanyModification() {
        if (this.accountableAccountInfo?.name) {
            return (
                this.accountableAccountInfo.name.startsWith("401") ||
                this.accountableAccountInfo.name.startsWith("411")
            );
        }
        return false;
    }

    get enableUserModification() {
        if (this.accountableAccountInfo?.name) {
            return (
                this.accountableAccountInfo.name.startsWith("421") ||
                this.accountableAccountInfo.name.startsWith("455") ||
                this.accountableAccountInfo.name.startsWith("467")
            );
        }
        return false;
    }

    get createBankTransactionMessages() {
        return this.selectedAccountingTransactionKeys.map((i) => this.accountingTransactions[i]);
    }

    get dataTableHeight() {
        const min = this.accountingTransactions.length;
        return "min(" + (26 * Math.max(min, 1) + 82) + "px, 60vh)";
    }

    miscellaneousOperationDialog: boolean = false;

    prefilledRows = [];

    async openMiscellaneousOperationDialog() {
        this.prefilledRows = [];
        let total = 0;
        const date = moment().format();

        let thirdPartyCompany;
        let bankAccount;
        if (this.accountableAccountInfo.thirdPartyCompanyName) {
            thirdPartyCompany = {
                id: "",
                name: this.accountableAccountInfo.thirdPartyCompanyName,
            };
            if (this.accountableAccountInfo.name.startsWith("411")) {
                const result = await this.$apollo.query({
                    query: CUSTOMER_COMPANIES,
                    variables: {
                        companyId: this.companyId,
                        search: this.accountableAccountInfo.thirdPartyCompanyName,
                    },
                });
                if (result.data.customerCompanies?.length) {
                    thirdPartyCompany = result.data.customerCompanies[0];
                } else {
                    debugger; // git-ignore
                }
            } else if (this.accountableAccountInfo.name.startsWith("401")) {
                const result = await this.$apollo.query({
                    query: SUPPLIER_COMPANIES,
                    variables: {
                        companyId: this.companyId,
                        search: this.accountableAccountInfo.thirdPartyCompanyName,
                    },
                });
                if (result.data.customerCompanies?.length) {
                    thirdPartyCompany = result.data.customerCompanies[0];
                } else {
                    debugger; // git-ignore
                }
            } else {
                debugger; // git-ignore
            }
        } else if (this.accountableAccountInfo.bankAccountName) {
            const result = await this.$apollo.query({
                query: BANK_ACCOUNT_FROM_NAME,
                variables: {
                    companyId: this.companyId,
                    name: this.accountableAccountInfo.bankAccountName,
                },
            });
            if (result.data.bankAccounts?.length) {
                bankAccount = result.data.bankAccounts[0];
            } else {
                debugger; // git-ignore
            }
        }

        for (const at of this.selectedAccountingTransactions) {
            const row = {
                date,
                accountableAccount: { name: this.accountableAccountInfo.name },
                user: undefined,
                thirdPartyCompany: undefined,
                bankAccount: undefined,
                wording: at.wording,
                // netAmount: 0.0,
                debit: at.credit,
                credit: at.debit,
            };
            if (this.accountableAccountInfo.userId) {
                row.user = {
                    id: this.accountableAccountInfo.userId,
                    firstname: this.accountableAccountInfo.firstname,
                    lastname: this.accountableAccountInfo.lastname,
                };
            }
            if (thirdPartyCompany) {
                row.thirdPartyCompany = thirdPartyCompany;
            }
            if (bankAccount) {
                row.bankAccount = bankAccount;
            }
            total += at.debit - at.credit;
            this.prefilledRows.push(row);
        }

        const row = {
            date,
            accountableAccount: { name: "" },
            wording: "",
            debit: total > 0 ? total : 0,
            credit: total < 0 ? -total : 0,
        };

        this.prefilledRows.push(row);
        this.miscellaneousOperationDialog = true;
    }

    get disabledCreateMO() {
        if (!this.selectedAccountingTransactions?.length) return true;
        if (this.selectionContainsATInAccountingClosing) return true;
        if (this.accountableAccountInfo?.name.startsWith("512")) return true;
        return false;
    }

    @Watch("dataTableHeight")
    @Emit("table-height")
    onDataTableHeight() {
        return this.dataTableHeight;
    }

    get addCheckBoxColumn() {
        if (this.accountingTransactions.length) {
            if (this.containsManualAccountingLines) {
                return true;
            }
            if (this.accountableAccountInfo.bnc) {
                return true;
            }
            if (this.accountableAccountInfo?.name) {
                if (!!this.accountableAccountReplacementRule) {
                    return true;
                }
                if (this.enableThirdPartyCompanyModification) {
                    return true;
                }
                if (this.enableUserModification) {
                    return true;
                }
                // if (this.accountableAccountInfo.name?.startsWith("471000")) {
                //     return item.object.type === 'bankTransaction' && !item.object.accountingItemImport
                // }
            }
        }
        return false;
    }

    get is471AccountSelected() {
        if (
            this.accountableAccountInfo?.name != undefined &&
            !this.accountableAccountInfo?.name?.startsWith("471000")
        ) {
            // Filter Rapprochement E|D is deactivated but we are not in a 471000 account
            return false;
        }
        return true;
    }

    get headers() {
        const headers = [];

        headers.push({
            text: "Type",
            value: "log",
            sortable: true,
            align: "left",
            filterable: {
                type: "equals",
                options: {
                    toText: (type) => {
                        return this.$t("accountingItem.type." + type.value);
                    },
                },
            },
        });
        headers.push({
            text: "Libellé",
            value: "wording",
            sortable: true,
            align: "left",
            filterable: { type: "includes" },
        });
        headers.push({
            text: "Date",
            value: "date",
            sortable: true,
            align: "center",
            width: "140px",
            filterable: { type: "includes", options: { input: "date" } },
        });
        if (this.isIncomingOutgoingPayment) {
            headers.push({
                text: "Date de règlement",
                value: "paymentDate",
                sortable: false,
                align: "center",
                width: "140px",
            });
        }
        if (this.enableLettering) {
            headers.push({
                text: "",
                value: "letteringSelection",
                sortable: false,
                align: "center" /*, width: "140px"*/,
                select: true,
            });

            headers.push({
                text: "Lettrage",
                value: "lettering",
                align: "left",
                filterable: {
                    type: "equals",
                    options: {
                        toText: (lettre) => {
                            console.log("lettre", lettre);
                            if (lettre?.text)
                                return lettre.text; //
                            else if (lettre) return lettre;
                            return "Sans Lettrage";
                        },
                        sort: "value",
                        customFilter: (items, filterKey) => {
                            const valuesToCompare = filterKey.map((x) => x.value);
                            const result = [];

                            for (const item of items) {
                                for (const vc of valuesToCompare) {
                                    if (vc === "") {
                                        if (!item.lettering || item.reconciliationToValidate) {
                                            result.push(item);
                                        }
                                    } else {
                                        if (
                                            item.lettering === vc &&
                                            !item.reconciliationToValidate
                                        ) {
                                            result.push(item);
                                        }
                                    }
                                }
                            }
                            return result;
                        },
                    },
                },
            });
        }
        headers.push({
            text: "Débit",
            value: "debit",
            sortable: true,
            align: "right",
            width: this.fourLastColumnsWidth ? this.fourLastColumnsWidth[0] + "px" : undefined,
            sum: true,
            filterable: { type: "startWith" },
        });
        headers.push({
            text: "Crédit",
            value: "credit",
            sortable: true,
            align: "right",
            width: this.fourLastColumnsWidth ? this.fourLastColumnsWidth[1] + "px" : undefined,
            sum: true,
            filterable: { type: "startWith" },
        });
        headers.push({
            text: "Solde",
            value: "balance",
            sortable: this.getAccountingTransactionBalance,
            align: "right",
            sum: true,
            width: this.fourLastColumnsWidth ? this.fourLastColumnsWidth[2] + "px" : undefined,
        });
        if (this.addEndColumn) {
            headers.push({
                text: "",
                value: "",
                sortable: false,
                align: "left",
                width: this.fourLastColumnsWidth ? this.fourLastColumnsWidth[3] + "px" : undefined,
            });
        }

        return headers;
    }

    compute(accountingTransactions) {
        this.accountingTransactions.sort((a, b) => {
            if (a.date < b.date) return -1;
            if (a.date > b.date) return 1;
            return 0;
        });
        this.afterCompute(accountingTransactions);
    }

    // majPress = false

    beforeMount() {
        // document.addEventListener('keydown', (event) => {
        //     const key = event.code || event.key
        //     if (key?.includes('Shift')) {
        //         this.majPress = true
        //     }
        // })
        // document.addEventListener('keyup', (event) => {
        //     const key = event.code || event.key
        //     if (key?.includes('Shift')) {
        //         this.majPress = false
        //     }
        // })
        if (this.inAccountingTransactions && !this.inAccountingTransactions.length) {
            this.compute(this.inAccountingTransactions);
            this.workingAccountingTransactions = this.inAccountingTransactions;
        }
    }

    // selectManyAndCheckLetterings(item, index) {
    //     // debugger
    //     if (this.lastSelected !== null && this.lastSelected !== undefined && this.majPress) {
    //         console.log("OK")
    //         console.log('lastSelected', this.lastSelected)
    //         console.log('index', index)
    //         if (this.lastSelected < index) {
    //             for (let i = this.lastSelected + 1; i <= index; i++) {
    //                 if (!this.accountingTransactions[i].selected) {
    //                     this.accountingTransactions[i].selected = !this.accountingTransactions[i].selected
    //                     this.checkLetterings(this.accountingTransactions[i])
    //                 }
    //             }
    //         } else {
    //             for (let i = this.lastSelected - 1; i >= index; i--) {
    //                 if (!this.accountingTransactions[i].selected) {
    //                     this.accountingTransactions[i].selected = !this.accountingTransactions[i].selected
    //                     this.checkLetterings(this.accountingTransactions[i])
    //                 }
    //             }
    //         }
    //     } else {
    //         this.checkLetterings(item)
    //     }

    //     this.lastSelected = index
    //     console.log('this.accountingTransactions', this.accountingTransactions)
    // }

    //
    // switch accountableAccount or thirdPartyCompany or user
    //

    onMultiTagguingApplied() {
        debugger;
        this.selectedAccountingTransactionKeys = [];
        this.refresh();
    }

    async setAccountableAccount(data) {
        await updateBankTransactionAccountableAccount(
            this.$apollo,
            this.$store,
            this.selectedCompany.id,
            this.selectedAccountingTransactions,
            data,
        );
        this.selectedAccountingTransactionKeys = [];
        this.refresh();
        this.refreshKey++;
    }

    disabledThirdPartyCompany() {
        if (!this.selectedAccountingTransactionKeys.length) {
            return true;
        }
        for (const item of this.selectedAccountingTransactionKeys) {
            if (
                (item.reconciliationStatus &&
                    (item.reconciliationStatus.notAllocatedAmountSigned === 0 ||
                        (item.accountingLines.length > 1 &&
                            item.accountingLines.every(
                                (al) => al.id && !al.accountableAccount?.name?.startsWith("512"),
                            )))) ||
                item.accountingItemImport ||
                item.status === "DELETED"
            ) {
                return true;
            }
        }
        return false;
    }

    miscelleanousAccountableAccount() {
        this.subSelectedAccountableAccounts = null;
        this.subAccountableAccountTitle = "Affecter à un compte";
    }
}

export default toNative(AccountableAccountBalanceDetail);
</script>
<style scoped>
.accountable-account-balance-detail {
    width: 100%;
}
.oss-th {
    padding: 0 16px;
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.6);
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
    height: 32px;
}
</style>
